import { useEffect, useState } from "react";
import { X, Users, Loader2, UserMinus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { initials } from "@/lib/utils-ros";
import { PartnerAssignmentPanel } from "@/components/revenueos/PartnerAssignment";

/**
 * ShareModal — invite collaborators/watchers + set specialty roles on any entity.
 * Backend: /api/share/{entity_type}/{entity_id} (GET + POST)
 */
export function ShareModal({ open, onOpenChange, entityType, entityId, entityLabel, partnerAssignment = false, onUpdated }) {
  const [state, setState] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get(`/share/${entityType}/${entityId}`),
      api.get("/users?limit=500").catch(() => ({ data: [] })),
    ]).then(([s, u]) => { setState(s.data); setAllUsers(u.data || []); });
  }, [open, entityType, entityId]);

  if (!open) return null;
  if (!state) return (
    <div className="fixed inset-0 z-50 bg-[#12324A]/45 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#F3FAFD] border border-[#BBD8E7] px-6 py-5 rounded-[22px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] flex items-center gap-2 text-[#526D7A]"><Loader2 size={14} className="animate-spin text-[#07539A]" /> Loading team…</div>
    </div>
  );

  const knownIds = new Set([
    state.primary_owner_id, state.owner_user_id,
    state.technical_expert_id, state.sme_id, state.founder_sponsor_id, state.delivery_lead_id,
    ...state.collaborators, ...state.watchers
  ].filter(Boolean));
  const candidates = allUsers.filter(u => !knownIds.has(u.id));

  async function add(role, uid) {
    setSaving(true);
    const body = {};
    if (role === "collab") body.add_collaborators = [uid];
    else if (role === "watch") body.add_watchers = [uid];
    else body[`set_${role}`] = uid;
    try {
      const { data } = await api.post(`/share/${entityType}/${entityId}`, body);
      setState(prev => ({ ...prev, ...data }));
      // reload the users list for display
      const users = await api.get(`/share/${entityType}/${entityId}`).then(r => r.data.users);
      setState(prev => ({ ...prev, users }));
      onUpdated?.();
      toast.success("Sharing updated");
    } catch { toast.error("Failed to update sharing"); }
    finally { setSaving(false); }
  }
  async function remove(role, uid) {
    setSaving(true);
    const body = {};
    if (role === "collab") body.remove_collaborators = [uid];
    else if (role === "watch") body.remove_watchers = [uid];
    else body[`set_${role}`] = null;
    try {
      const { data } = await api.post(`/share/${entityType}/${entityId}`, body);
      setState(prev => ({ ...prev, ...data }));
      onUpdated?.();
      toast.success("Removed");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  }

  const umap = Object.fromEntries((state.users || []).map(u => [u.id, u]));
  const nameOf = (id) => umap[id]?.name || allUsers.find(u => u.id === id)?.name || id?.slice(0, 8);

  const SPECIALTY = [
    ["primary_owner_id", "Primary Owner"],
    ["technical_expert_id", "Technical Expert"],
    ["sme_id", "SME"],
    ["founder_sponsor_id", "Founder Sponsor"],
    ["delivery_lead_id", "Delivery Lead"],
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#12324A]/45 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => onOpenChange(false)} data-testid="share-modal">
      <div className="w-full max-w-2xl bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] shrink-0">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#C9DCE6] flex items-center justify-center text-[#07539A]"><Users size={15} /></div>
          <div className="font-display text-[18px] flex-1 text-[#12324A] truncate">Manage team · {entityLabel || entityType}</div>
          <button onClick={() => onOpenChange(false)} data-testid="share-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A]"><X size={15} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Specialty roles */}
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#07539A] font-semibold mb-2">Named roles</div>
            <div className="space-y-1.5">
              {SPECIALTY.map(([field, label]) => {
                const uid = state[field];
                return (
                  <div key={field} className="flex items-center gap-3 p-2.5 rounded-[11px] border border-[#D7E8F0] bg-[#F8FCFE]">
                    <div className="text-[10px] uppercase tracking-wider text-[#526D7A] w-32 shrink-0">{label}</div>
                    <div className="flex-1 min-w-0 text-[12px] text-[#12324A]">
                      {uid ? nameOf(uid) : <span className="text-[#90A4AE]">— unassigned —</span>}
                    </div>
                    <select
                      value={uid || ""}
                      disabled={saving}
                      onChange={e => {
                        const v = e.target.value;
                        if (!v) remove(field, uid);
                        else add(field, v);
                      }}
                      data-testid={`share-select-${field}`}
                      className="h-9 min-w-[11rem] px-2 text-[11px] bg-white border border-[#D7E8F0] rounded-[10px] focus:border-[#07539A] outline-none text-[#12324A]"
                    >
                      <option value="">— none —</option>
                      {allUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Collaborators */}
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#07539A] font-semibold mb-2">Collaborators ({state.collaborators.length})</div>
            <div className="space-y-1 mb-2">
              {state.collaborators.map(uid => (
                <div key={uid} className="flex items-center gap-2 p-2 rounded-[11px] bg-[#E7F5FB] border border-[#D7E8F0]" data-testid={`collab-${uid}`}>
                  <div className="w-7 h-7 rounded-full bg-[#07539A] !text-white flex items-center justify-center text-[10px] font-mono">{initials(nameOf(uid))}</div>
                  <span className="flex-1 text-[12px]">{nameOf(uid)}</span>
                  <button onClick={() => remove("collab", uid)} data-testid={`remove-collab-${uid}`} className="text-[var(--text-dim)] hover:text-[var(--danger)]"><UserMinus size={12} /></button>
                </div>
              ))}
              {state.collaborators.length === 0 && <div className="text-[11px] text-[#90A4AE]">No collaborators yet.</div>}
            </div>
            <select
              onChange={e => e.target.value && add("collab", e.target.value)}
              value=""
              disabled={saving}
              data-testid="add-collab-select"
              className="w-full h-10 text-[12px] bg-[#F8FCFE] border border-[#D7E8F0] rounded-[11px] px-3 focus:border-[#07539A] outline-none text-[#12324A]"
            >
              <option value="">+ Add collaborator…</option>
              {candidates.map(u => <option key={u.id} value={u.id}>{u.name || u.email} · {u.role}</option>)}
            </select>
          </div>

          {/* Watchers */}
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#07539A] font-semibold mb-2">Watchers ({state.watchers.length})</div>
            <div className="space-y-1 mb-2">
              {state.watchers.map(uid => (
                <div key={uid} className="flex items-center gap-2 p-2 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0]" data-testid={`watch-${uid}`}>
                  <div className="w-7 h-7 rounded-full bg-[#78909C] !text-white flex items-center justify-center text-[10px] font-mono">{initials(nameOf(uid))}</div>
                  <span className="flex-1 text-[12px]">{nameOf(uid)}</span>
                  <button onClick={() => remove("watch", uid)} data-testid={`remove-watch-${uid}`} className="text-[var(--text-dim)] hover:text-[var(--danger)]"><UserMinus size={12} /></button>
                </div>
              ))}
              {state.watchers.length === 0 && <div className="text-[11px] text-[#90A4AE]">No watchers yet.</div>}
            </div>
            <select
              onChange={e => e.target.value && add("watch", e.target.value)}
              value=""
              disabled={saving}
              data-testid="add-watch-select"
              className="w-full h-10 text-[12px] bg-[#F8FCFE] border border-[#D7E8F0] rounded-[11px] px-3 focus:border-[#07539A] outline-none text-[#12324A]"
            >
              <option value="">+ Add watcher…</option>
              {candidates.map(u => <option key={u.id} value={u.id}>{u.name || u.email} · {u.role}</option>)}
            </select>
          </div>

          {partnerAssignment && (
            <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#07539A] font-semibold mb-2">Partner &amp; commission</div>
              <div className="rounded-[12px] border border-[#BBD8E7] bg-[#E7F5FB] p-3">
                <PartnerAssignmentPanel entityType={entityType} entityId={entityId} compact accent="navy" />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 pt-4 border-t border-[#C9DCE6] bg-[#F3FAFD] shrink-0">
          <button onClick={() => onOpenChange(false)} disabled={saving} data-testid="share-done" className="h-11 px-6 rounded-[14px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[13px] font-medium shadow-[0_7px_18px_rgba(7,83,154,0.16)] disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 size={13} className="animate-spin" />}{saving ? "Saving…" : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
}
